import { useState } from "react";
import { useEmbeddedDashboards } from "../../lib/queries";
import Sparkline, { fakeSparkline } from "../cockpit/Sparkline";

interface EmbeddedDashboardPaneProps {
  projectName: string;
}

export default function EmbeddedDashboardPane({ projectName }: EmbeddedDashboardPaneProps) {
  const { data: embeds = [] } = useEmbeddedDashboards(projectName);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  if (embeds.length === 0) {
    return (
      <div
        className="aero-glass mx-3 mt-3 mb-4 px-4 py-3 text-xs"
        style={{
          borderRadius: 18,
          fontFamily: "Fraunces, serif",
          fontStyle: "italic",
          color: "var(--plum)",
          opacity: 0.7,
        }}
      >
        no embedded dashboards configured for {projectName}
      </div>
    );
  }

  return (
    <div className="mx-3 mt-3 mb-4 flex flex-col gap-3">
      {embeds.map((e) => {
        const isCollapsed = collapsed[e.id] ?? false;
        const hostname = (() => {
          try {
            return new URL(e.url).hostname;
          } catch {
            return e.url;
          }
        })();

        return (
          <div
            key={e.id}
            className="aero-glass flex flex-col"
            style={{ borderRadius: 22 }}
          >
            <div
              className="flex items-center justify-between px-4 py-3"
              style={{ borderBottom: "1px solid rgba(255,31,165,0.15)" }}
            >
              <div className="flex items-center gap-3">
                <span
                  className="glossy-radial-bubble pulse-dot"
                  style={{ width: 9, height: 9, color: "#7ec699" }}
                />
                <span
                  style={{
                    fontFamily: "Fraunces, serif",
                    fontStyle: "italic",
                    fontWeight: 600,
                    fontSize: 14,
                    color: "var(--ink)",
                  }}
                >
                  {e.label}
                </span>
                <span
                  className="font-eyebrow"
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    color: "var(--plum)",
                    opacity: 0.6,
                  }}
                >
                  {hostname}
                </span>
                <Sparkline data={fakeSparkline(e.id, 7)} width={52} height={14} stroke="#FF1FA5" className="pink-glow" />
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={e.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="glossy-pill glossy-pill--ghost glossy-pill--small"
                >
                  open ↗
                </a>
                <button
                  type="button"
                  onClick={() => setCollapsed((c) => ({ ...c, [e.id]: !isCollapsed }))}
                  className="glossy-pill glossy-pill--ghost glossy-pill--small"
                >
                  {isCollapsed ? "expand" : "collapse"}
                </button>
              </div>
            </div>
            {!isCollapsed ? (
              e.iframe_safe ? (
                <iframe
                  src={e.url}
                  title={e.label}
                  sandbox="allow-same-origin allow-scripts allow-forms"
                  loading="lazy"
                  style={{
                    width: "100%",
                    height: e.height_px ?? 600,
                    border: 0,
                    borderRadius: "0 0 21px 21px",
                    background: "white",
                  }}
                />
              ) : (
                <div
                  className="flex flex-col items-center justify-center gap-2 px-3 py-6 text-xs"
                  style={{
                    fontFamily: "Fraunces, serif",
                    fontStyle: "italic",
                    color: "var(--plum)",
                  }}
                >
                  <span>This service blocks iframing.</span>
                  <a
                    href={e.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="glossy-pill glossy-pill--small"
                  >
                    Open {e.label} →
                  </a>
                </div>
              )
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
